/* eslint-disable no-underscore-dangle */
import { useRequest } from 'ahooks';
import {
  Alert,
  Button,
  Col,
  Form,
  Image,
  Input,
  message,
  Radio,
  Row,
  Select,
  Space,
  Spin,
  Typography,
} from 'antd';
import LocaleContext from 'locales';
import moment from 'moment';
import React, { useContext, useEffect, useState } from 'react';
import {
  getUserContact,
  saveOtpSecretkey,
  twoFactorAuth,
  twoFactorCodeGenerate,
} from 'services/websocket/auth';

/**
 * 기본 OTP 인증: Google/Microsoft Authenticator
 * @param {object} credential {success(인증 성공 여부), idpw(id/pw 인증 정보), twoFactor}
 * @param {function} loginDispatch loginState의 dispatch
 * @returns ReactNode
 */

function MultiFactor({ credential, loginDispatch }) {
  const {
    idpw,
    twoFactor: { type, channel = [], data },
  } = credential;
  const [error, setError] = useState(undefined);
  const [isRequireSecretKey, setIsRequireSecretKey] = useState(type === 'require-secret-key'); // OTP 설정 키 설정이 필요한지 여부
  const [loading, setLoading] = useState(false);
  const [generated, setGenerated] = useState(false); // 인증코드 생성 결과 {result: true, expireDate: "2022-05-02T08:56:06.784Z"}
  const [selectedChannel, setSelectedChannel] = useState(undefined); // 현재 선택한 채널의 정보 {channel: string, contact: string | []}
  const [expired, setExpired] = useState(false); // 인증코드 만료 여부
  const { locale } = useContext(LocaleContext);
  const [contactInfo, setContactInfo] = useState({}); // 채널별 연락 정보
  const [form] = Form.useForm();

  useEffect(() => {
    if (expired) setError(locale.resource['txt.auth-expired']);
  }, [expired]);

  const contactFetch = useRequest(getUserContact, {
    defaultParams: [{ userId: idpw.userId }],
    onSuccess: (resData) => {
      if (!resData.success) {
        message.error(locale.resource[resData.msg]);
        return;
      }
      setContactInfo(resData.data);
    },
  });

  // FC: 인증코드 생성 요청 폼
  const CodeGenerateForm = () => {
    const onFinish = async (values) => {
      setError(undefined); // sumit 할때마다 error를 clear 한다.
      setLoading(true);

      const payload = {
        ...values,
        loginInfo: {
          ...idpw,
          sendOption: {
            channel: values.channel,
            locale: localStorage.getItem('locale'),
          },
        },
      };
      // enmobile인 경우 선택한 디바이스의 ID를 같이 보내준다.
      if (selectedChannel.channel === 'enmobile') {
        payload.deviceId = form.getFieldValue('deviceId');
      }
      try {
        const res = await twoFactorCodeGenerate(payload);
        if (res.result) {
          setGenerated(res);
        } else {
          setError(locale.resource[res.msg]);
        }
      } catch (e) {
        setError(e?.response?.data?.msg);
      } finally {
        setLoading(false);
      }
    };

    // 인증 방식 옵션들
    const channelOptions = () =>
      channel.map((el) => ({ label: locale.resource[`txt.${el}`], value: el }));

    return (
      <Spin spinning={contactFetch.loading}>
        <Form style={{ width: '100%' }} onFinish={onFinish} form={form}>
          <div>{locale.resource['txt.two-factor']}</div>
          <Form.Item
            label={locale.resource['txt.auth-type']}
            name="channel"
            style={{ marginBlock: 8 }}
          >
            <Radio.Group
              options={channelOptions()}
              onChange={(e) => {
                const ch = e.target.value;
                if (ch === 'email') {
                  setSelectedChannel({
                    channel: ch,
                    contact: contactInfo.mail || locale.resource['txt.no-registered-information'],
                  });
                } else if (ch === 'exmail') {
                  setSelectedChannel({
                    channel: ch,
                    contact:
                      contactInfo.externalMail || locale.resource['txt.no-registered-information'],
                  });
                } else if (ch === 'enmobile') {
                  setSelectedChannel({
                    channel: ch,
                    contact: contactInfo.deviceList || [],
                  });
                } else {
                  setSelectedChannel({
                    channel: ch,
                    contact:
                      contactInfo.telephoneNumber ||
                      locale.resource['txt.no-registered-information'],
                  });
                }
              }}
            />
          </Form.Item>
          {selectedChannel && selectedChannel.channel === 'enmobile' && (
            <div style={{ marginTop: 8 }}>
              <Typography.Paragraph>{locale.resource['txt.enmobile-require']}</Typography.Paragraph>
              <Form.Item
                label="Device"
                name="deviceId"
                required
                rules={[{ required: true, message: locale.resource['txt.required-field'] }]}
              >
                <Select
                  style={{ width: '100%' }}
                  options={selectedChannel?.contact.map((el) => ({
                    label: el.phoneModel,
                    value: el.deviceId,
                  }))}
                />
              </Form.Item>
            </div>
          )}
          {selectedChannel?.contact && selectedChannel.channel !== 'enmobile' && (
            <Alert
              style={{
                textAlign: 'center',
                backgroundColor: '#E8ECEF',
                borderColor: '#E8ECEF',
                marginBlock: 8,
              }}
              message={selectedChannel.contact}
            />
          )}

          <Button
            type="primary"
            htmlType="submit"
            block
            style={{ marginTop: 24 }}
            loading={loading}
          >
            {locale.resource['txt.send-auth-code']}
          </Button>
        </Form>
      </Spin>
    );
  };

  // 등록 완료되었음을 서버에 알린다. 서버는 등록 완료 메시지가 왔을 때 /tmp/user-secret-keys 폴더에 id.2fa 형태로 저장한다.
  const onSaveOtpSecretkey = async () => {
    setLoading(true);
    try {
      const res = await saveOtpSecretkey({ userId: idpw.userId, secretKey: data?.key });
      if (res.success) {
        setIsRequireSecretKey(false);
      } else {
        setError(res.message);
      }
      // eslint-disable-next-line no-shadow
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  // 인증 코드를 검증한다.
  const onFinish = async (values) => {
    setError(undefined); // sumit 할때마다 error를 clear 한다.
    setLoading(true);
    const payload = {
      ...values,
      ...idpw,
      channel: selectedChannel.channel,
      locale: localStorage.getItem('locale') || 'ko-KR',
    };

    try {
      const res = await twoFactorAuth(payload);
      if (res.status === 'success') {
        localStorage.setItem('token', res.token);
        loginDispatch({ type: 'set-credential', payload: { ...credential, success: true } });
      } else {
        setError(locale.resource[res.message] || res.error);
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  // OTP 설정 컴포넌트
  function SetSecretkey() {
    return (
      <Space direction="vertical" size={0} style={{ alignItems: 'center' }}>
        <Typography.Paragraph style={{ whiteSpace: 'pre-line' }}>
          {locale.resource['two-factor-auth.otp.register-guide']}
        </Typography.Paragraph>
        <Image src={data?.qrcode} width={180} preview={false} />
        <div style={{ wordBreak: 'break-word' }}>
          {locale.resource['two-factor-auth.otp.setup-key']}: {data?.key}
        </div>
        <Button
          type="primary"
          style={{ marginTop: 16, width: 240 }}
          onClick={onSaveOtpSecretkey}
          loading={loading}
        >
          {locale.resource['txt.registration-complete']}
        </Button>
      </Space>
    );
  }

  // OTP 인증코드 입력 컴포넌트
  function InputCodeForm() {
    if (selectedChannel.channel === 'enmobile') {
      return (
        <div style={{ textAlign: 'center' }}>
          {!expired ? (
            <>
              <Typography.Paragraph>{locale.resource['txt.enmobile-waiting']}</Typography.Paragraph>
              <RemainedTime generated={generated} setExpired={setExpired} />
              <Button type="primary" block onClick={() => onFinish()} loading={loading}>
                {locale.resource['txt.auth-complete']}
              </Button>
            </>
          ) : (
            <Button block onClick={() => window.location.reload()}>
              {locale.resource['login.submitText']}
            </Button>
          )}
        </div>
      );
    }
    return (
      <Form onFinish={onFinish}>
        <Typography.Paragraph style={{ whiteSpace: 'pre-line' }}>
          {locale.resource['two-factor-auth.otp.require-code']}
        </Typography.Paragraph>
        <Form.Item
          name="otpNumber"
          required
          rules={[{ required: true, message: locale.resource['txt.required-field'] }]}
        >
          <Input
            size="large"
            placeholder={locale.resource['txt.enter-code']}
            autoFocus
            autoComplete="off"
          />
        </Form.Item>
        <Button
          type="primary"
          size="large"
          block
          htmlType="submit"
          style={{ marginTop: 16 }}
          loading={loading}
        >
          {locale.resource['txt.ok']}
        </Button>
      </Form>
    );
  }

  let returnForm;
  if (isRequireSecretKey) {
    returnForm = <SetSecretkey />;
  } else if (generated.result) {
    returnForm = <InputCodeForm />;
  } else {
    returnForm = <CodeGenerateForm />;
  }

  return (
    <Row gutter={[0, 16]}>
      {error && (
        <Col span={24}>
          <Alert type="error" message={<div style={{ whiteSpace: 'pre-line' }}>{error}</div>} />
        </Col>
      )}
      <Col span={24}>{returnForm}</Col>
    </Row>
  );
}

/**
 * 발급된 인증코드의 남은 시간을 리턴하는 함수
 * 인증코드 유효 기간(3분) 동안 1초마다 렌더링 되기 때문에 별도 Component로 뺌
 * @param {boolean} generated 인증코드 발급 여부에 따라 타이머를 동작시킨다.
 * @param {boolean} setExpired 인증코드의 유효 시간이 만료되었는지 판별한다.
 * @returns ReactNode
 */
function RemainedTime({ generated, setExpired }) {
  const [result, setResult] = useState(undefined); // 만료시각과 현재시각 비교 값(예: 2:58)

  // 만료시각이 임박했는가?
  const impend = result?.split(':')[0] === '0' && Number(result.split(':')[1]) < 10 && true;

  // generated 되면 인증시간 타이머를 동작시킨다.
  useEffect(() => {
    const timer = setInterval(() => {
      if (generated.result) {
        const expireTime = moment(generated.expireDate); // 서버로 부터 받은 만료 시각
        const now = moment();
        const diff = moment.duration(expireTime.diff(now));

        if (diff._milliseconds > 0) {
          setResult(`${diff._data.minutes}:${diff._data.seconds}`);
        } else {
          setResult('0:0');
          setExpired(true);
          clearInterval(timer);
        }
      }
    }, 1000);
    return () => {
      clearInterval(timer);
    };
  }, [generated]);

  return (
    <Typography.Paragraph style={{ fontSize: 16 }} type={impend ? 'danger' : null}>
      {result}
    </Typography.Paragraph>
  );
}

export default MultiFactor;
